"use client";

import { reportAccessibilityIssueAction } from "@/app/actions";
import { getCaptchaToken } from "@/services/google";
import { loadScript } from "@/utils/scripts";
import classNames from "classnames";
import { useId, useState } from "react";
import FormErrorSummary from "./form/form-error-summary";
import InputField from "./form/input-field";
import RequiredFieldsNote from "./form/required-fields-note";

const AccessibilityFeedbackForm = () => {
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const siteKey = process.env.NEXT_PUBLIC_ACCESSIBILITY_CAPTCHA_SITE_KEY;

  const descriptionId = useId();
  const descriptionErrorId = useId();

  const onSubmit = async () => {
    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setDescriptionError("Beskriv bristen du har upptäckt.");
      return;
    }

    setDescriptionError("");
    setSubmitError("");
    setIsSubmitting(true);

    try {
      let token = "";
      if (siteKey) {
        await loadScript({
          src: `https://www.google.com/recaptcha/api.js?render=${siteKey}`,
          async: true,
          defer: true,
          waitFor: () =>
            new Promise<void>((resolve, reject) => {
              const test = (tries: number) => {
                if (typeof (window as any)?.grecaptcha?.execute !== "undefined") {
                  resolve();
                } else if (tries > 40) {
                  reject();
                } else {
                  setTimeout(() => test(tries + 1), tries < 10 ? 100 : 350);
                }
              };

              test(0);
            }),
        });

        token = await getCaptchaToken("report_accessibility_issue", siteKey);
      } else {
        token = "captcha-disabled";
      }

      const response = await reportAccessibilityIssueAction(
        trimmedDescription,
        email.trim(),
        token
      );

      if (response?.success) {
        setIsSubmitted(true);
      } else {
        setSubmitError(
          "Något gick fel. Prova igen, eller kontakta oss via telefon eller mejl ovan."
        );
      }
    } catch {
      setSubmitError(
        "Något gick fel. Prova igen, eller kontakta oss via telefon eller mejl ovan."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <p role="status" className="max-w-[586px]">
        Tack, din rapport är skickad. Om du angav en e-postadress återkommer
        vi till dig.
      </p>
    );
  }

  return (
    <form
      className="max-w-[586px]"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <RequiredFieldsNote />
      <FormErrorSummary hasError={Boolean(descriptionError || submitError)} />

      <div className="mb-4 mt-4">
        <label htmlFor={descriptionId} className="block mb-1 font-bold">
          Beskriv bristen{" "}
          <span>
            *<span className="sr-only">(Obligatoriskt)</span>
          </span>
        </label>

        <textarea
          id={descriptionId}
          name="description"
          rows={5}
          required
          aria-describedby={descriptionError ? descriptionErrorId : undefined}
          aria-invalid={Boolean(descriptionError)}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            if (descriptionError) setDescriptionError("");
          }}
          className={classNames(
            "text-[16px] bg-sand-200 rounded-sm text-gray-t00 p-4 w-full border placeholder:text-gray-400 placeholder:opacity-75",
            descriptionError ? "border-red-500" : "border-sand-600"
          )}
          placeholder="Vad är problemet, och var på webbplatsen upptäckte du det?"
        />

        {descriptionError && (
          <p id={descriptionErrorId} className="mt-0.5 text-sm text-red-700 dark:text-static-white">
            {descriptionError}
          </p>
        )}
      </div>

      <div className="mb-4">
        <InputField
          label="E-postadress (frivilligt, om du vill ha svar)"
          name="email"
          type="email"
          value={email}
          onChange={(e: any) => setEmail(e.target.value)}
          autoComplete="email"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn btn-primary h-12 px-6"
      >
        {isSubmitting ? "Skickar…" : "Skicka"}
      </button>

      {submitError && (
        <p className="mt-4 text-red-700 dark:text-static-white">
          {submitError}
        </p>
      )}
    </form>
  );
};

export default AccessibilityFeedbackForm;
